"use client";

import { MediaAsset } from "@/types/menu";
import Image from "next/image";
import { FileText, Images, Upload, Pencil } from "lucide-react";
import { useState, useEffect, useCallback } from "react";
import dynamic from "next/dynamic";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { SupabaseClient } from "@supabase/supabase-js";
import { Database } from "@/lib/supabase/client";
import { useDropzone } from "react-dropzone";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import useMenuStore from "@/store/menuStore";
import { v4 as uuidv4 } from "uuid";
import { Progress } from "@/components/ui/progress";
import { useTranslations } from "next-intl";

// Define the props interface for PDFPreview
interface PDFPreviewProps {
  url: string;
}

// Define props for MediaUploaderDialog
interface MediaUploaderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuId: string;
  supabase: SupabaseClient<Database>;
}

// Define props for MediaManagerDialog
interface MediaManagerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  menuId: string;
  supabase: SupabaseClient<Database>;
}

// Define file upload status structure
interface FileUploadStatus {
  id: string;
  file: File;
  status: "uploading" | "success" | "error";
  progress: number;
  error?: string;
}

// Dynamically import PDFViewer component to avoid SSR issues
const PDFPreview = dynamic<PDFPreviewProps>(
  () => import("@/components/menu/PDFPreview"),
  {
    ssr: false,
    loading: () => (
      <div className="w-full h-full flex items-center justify-center">
        <FileText className="h-16 w-16 text-muted-foreground mb-2" />
      </div>
    ),
  }
);

// Dynamically import MediaUploader to avoid SSR issues
const MediaUploader = dynamic(() => import("@/components/menu/MediaUploader"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  ),
});

// Dynamically import MediaManager to avoid SSR issues
const MediaManager = dynamic(() => import("@/components/menu/MediaManager"), {
  ssr: false,
  loading: () => (
    <div className="flex items-center justify-center h-40">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
    </div>
  ),
});

// Media Uploader Dialog Component
function MediaUploaderDialog({
  open,
  onOpenChange,
  menuId,
  supabase,
}: MediaUploaderDialogProps) {
  const t = useTranslations();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 sm:p-6 h-auto w-[100dvw] sm:w-auto sm:min-w-[500px] sm:max-w-[600px] flex flex-col space-y-0 overflow-hidden">
        <DialogHeader className="flex-shrink-0 p-6 pb-2 sm:p-0 sm:pb-2">
          <DialogTitle>{t("MenuBanner.uploadMedia")}</DialogTitle>
          <DialogDescription>
            {t("MenuBanner.uploadMediaDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-grow">
          <MediaUploader
            menuId={menuId}
            supabase={supabase}
            onUploadComplete={() => onOpenChange(false)}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Media Manager Dialog Component
function MediaManagerDialog({
  open,
  onOpenChange,
  menuId,
  supabase,
}: MediaManagerDialogProps) {
  const t = useTranslations();
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="p-0 sm:p-6 h-[100dvh] w-[100dvw] sm:h-auto sm:w-auto sm:min-w-[500px] sm:max-w-[600px] sm:max-h-[85vh] flex flex-col space-y-0 overflow-hidden">
        <DialogHeader className="flex-shrink-0 p-6 pb-2 sm:p-0 sm:pb-2">
          <DialogTitle>{t("MenuBanner.mediaManager")}</DialogTitle>
          <DialogDescription className="text-balance">
            {t("MenuBanner.mediaManagerDescription")}
          </DialogDescription>
        </DialogHeader>
        <div className="overflow-y-auto flex-grow">
          <MediaManager menuId={menuId} supabase={supabase} />
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface NewMenuBannerProps {
  mediaAssets: MediaAsset[];
  defaultMediaId: string | null;
  onClick: (index: number) => void;
  onMediaManagerClick?: () => void;
  menuId?: string;
  supabase?: SupabaseClient<Database>;
  isEditable?: boolean;
  menu?: {
    originalImageUrl?: string;
    id?: string;
    title?: Record<string, string>;
    supportedLanguages?: string[];
    [key: string]: unknown; // Allow any additional properties with unknown type
  };
  uiLabels?: {
    viewMedia?: string;
  };
}

export default function NewMenuBanner({
  mediaAssets,
  defaultMediaId,
  onClick,
  onMediaManagerClick,
  menuId,
  supabase,
  menu,
  isEditable = false,
  uiLabels,
}: NewMenuBannerProps) {
  const t = useTranslations();
  // State to track all assets including legacy PDFs
  const [allAssets, setAllAssets] = useState<MediaAsset[]>([]);
  const [showMediaUploader, setShowMediaUploader] = useState(false);
  const [showMediaManager, setShowMediaManager] = useState(false);
  const { toast } = useToast();
  // Add menuStore's addMediaAsset function
  const addMediaAsset = useMenuStore((state) => state.addMediaAsset);
  // Add state to track uploading files
  const [uploadingFiles, setUploadingFiles] = useState<FileUploadStatus[]>([]);
  // Add state for mobile detection
  const [isMobileDevice, setIsMobileDevice] = useState(false);

  // Detect mobile device on component mount
  useEffect(() => {
    const checkIfMobile = () => {
      // Fix TypeScript error by using a type assertion that avoids 'any'
      const userAgent =
        navigator.userAgent ||
        navigator.vendor ||
        (window as unknown as { opera?: string }).opera ||
        "";
      const mobileRegex =
        /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini/i;
      return mobileRegex.test(userAgent.toLowerCase());
    };

    setIsMobileDevice(checkIfMobile());
  }, []);

  // Process legacy PDF URL on component mount or when dependencies change
  useEffect(() => {
    // Check if this is a legacy PDF URL from original_image_url
    const legacyPdfUrl =
      menu?.originalImageUrl &&
      menu.originalImageUrl.toLowerCase().endsWith(".pdf")
        ? menu.originalImageUrl
        : null;

    // If there's a legacy image URL that's not a PDF, treat it as an image asset
    const legacyImageUrl =
      menu?.originalImageUrl &&
      !menu.originalImageUrl.toLowerCase().endsWith(".pdf")
        ? menu.originalImageUrl
        : null;

    // Create a list to hold all assets
    const combinedAssets = [...mediaAssets];

    // Create a temporary asset for the legacy PDF if it exists
    if (legacyPdfUrl) {
      const legacyAsset: MediaAsset = {
        id: "legacy-pdf",
        url: legacyPdfUrl,
        type: "pdf",
        filename: "Menu PDF", // No "legacy" label for users
        uploadedAt: new Date().toISOString(),
      };

      // Only add the legacy PDF if there isn't already a PDF with the same URL
      if (!mediaAssets.some((asset) => asset.url === legacyPdfUrl)) {
        combinedAssets.push(legacyAsset);
      }
    }

    // Create a temporary asset for the legacy image if it exists
    if (legacyImageUrl) {
      const legacyAsset: MediaAsset = {
        id: "legacy-image",
        url: legacyImageUrl,
        type: "image",
        filename: "Menu Image", // No "legacy" label for users
        uploadedAt: new Date().toISOString(),
      };

      // Only add the legacy image if there isn't already an image with the same URL
      if (!mediaAssets.some((asset) => asset.url === legacyImageUrl)) {
        combinedAssets.push(legacyAsset);
      }
    }

    setAllAssets(combinedAssets);
  }, [mediaAssets, menu?.originalImageUrl]);

  // Find the default asset (image or PDF) based on defaultMediaId
  const findDefaultAsset = () => {
    if (defaultMediaId && allAssets.length > 0) {
      const defaultAsset = allAssets.find(
        (asset) => asset.id === defaultMediaId
      );
      if (defaultAsset) {
        return {
          asset: defaultAsset,
          index: allAssets.indexOf(defaultAsset),
        };
      }
    }
    return null;
  };

  // Find the first image asset if no default is set
  const findFirstImageAsset = () => {
    if (allAssets.length > 0) {
      const firstImage = allAssets.find((asset) => asset.type === "image");
      if (firstImage) {
        return {
          asset: firstImage,
          index: allAssets.indexOf(firstImage),
        };
      }
    }
    return null;
  };

  // Find the first PDF asset if no image is available
  const findFirstPdfAsset = () => {
    if (allAssets.length > 0) {
      const firstPdf = allAssets.find((asset) => asset.type === "pdf");
      if (firstPdf) {
        return {
          asset: firstPdf,
          index: allAssets.indexOf(firstPdf),
        };
      }
    }
    return null;
  };

  // Determine the display asset using a priority order
  const displayAsset =
    findDefaultAsset() || findFirstImageAsset() || findFirstPdfAsset();

  const handleViewMedia = () => {
    if (allAssets.length === 0) return;

    // Always start the carousel from the first image (index 0)
    onClick(0);
  };

  // Create a dedicated handler for the view button that stops propagation
  // to prevent double-firing of the parent button's click handler
  const handleViewButtonClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    handleViewMedia();
  };

  // Handle media dialog button click
  const handleMediaDialogClick = (e: React.MouseEvent) => {
    // Always stop propagation
    if (e) {
      e.stopPropagation();
    }

    // If an external handler is provided, use that
    if (onMediaManagerClick) {
      onMediaManagerClick();
    }
    // Otherwise if we have the menuId and supabase instance, open the media manager dialog
    else if (menuId && supabase) {
      setShowMediaManager(true);
    }
  };

  // Handle "Upload media" button click - specifically for uploading new media
  const handleUploadClick = (e: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }

    if (menuId && supabase) {
      setShowMediaUploader(true);
    }
  };

  // Maximum file sizes
  const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10MB for images
  const MAX_PDF_SIZE = 20 * 1024 * 1024; // 20MB for PDFs
  const MAX_VIDEO_SIZE = 50 * 1024 * 1024; // 50MB for videos

  // Helper function to sanitize file names
  const sanitizeFileName = useCallback((fileName: string): string => {
    // Get the file extension
    const extension = fileName.split(".").pop()?.toLowerCase() || "";

    // Create a URL-safe base name
    const safeName = encodeURIComponent(
      fileName.slice(0, fileName.lastIndexOf("."))
    )
      .replace(/%/g, "")
      .replace(/[^a-zA-Z0-9-_]/g, "-")
      .replace(/-+/g, "-")
      .replace(/^-|-$/g, "");

    // Create a unique filename with timestamp to prevent collisions
    const timestamp = Date.now();
    const uniqueName = `${timestamp}-${safeName || "untitled"}.${extension}`;

    return uniqueName;
  }, []);

  // Validate file type and size
  const validateFile = useCallback(
    (file: File) => {
      // Check file type - accept images, PDFs, and videos
      if (
        !file.type.match(
          /^(image\/(jpeg|png|webp)|application\/pdf|video\/(mp4|webm|ogg))$/
        )
      ) {
        return {
          valid: false,
          error:
            "Only JPG, PNG, WebP images, MP4/WebM/OGG videos and PDF files are allowed",
        };
      }

      // Check file size
      const isImage = file.type.startsWith("image/");
      const isPdf = file.type === "application/pdf";
      const isVideo = file.type.startsWith("video/");

      let maxSize = MAX_IMAGE_SIZE;
      if (isPdf) maxSize = MAX_PDF_SIZE;
      if (isVideo) maxSize = MAX_VIDEO_SIZE;

      if (file.size > maxSize) {
        const maxSizeMB = maxSize / (1024 * 1024);
        return {
          valid: false,
          error: `File too large. Maximum size for ${
            isImage ? "images" : isPdf ? "PDFs" : "videos"
          } is ${maxSizeMB}MB`,
        };
      }

      return { valid: true };
    },
    [MAX_IMAGE_SIZE, MAX_PDF_SIZE, MAX_VIDEO_SIZE]
  );

  // Function to upload a single file
  const uploadFile = useCallback(
    async (file: File) => {
      if (!menuId || !supabase) return;

      const fileId = uuidv4();

      // Add file to uploading state
      setUploadingFiles((prev) => [
        ...prev,
        {
          id: fileId,
          file,
          status: "uploading",
          progress: 0,
        },
      ]);

      try {
        // Validate file
        const validation = validateFile(file);
        if (!validation.valid) {
          throw new Error(validation.error);
        }

        // Generate ID for the media asset
        const mediaAssetId = uuidv4();

        // Sanitize filename
        const sanitizedName = sanitizeFileName(file.name);

        // Create Supabase storage path
        const storagePath = `menus/${menuId}/media/${mediaAssetId}-${sanitizedName}`;

        // Simulate incremental upload progress
        const progressInterval = setInterval(() => {
          setUploadingFiles((prev) =>
            prev.map((item) =>
              item.id === fileId
                ? {
                    ...item,
                    progress: Math.min(item.progress + 10, 90),
                  }
                : item
            )
          );
        }, 300);

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from("menus")
          .upload(storagePath, file);

        clearInterval(progressInterval);

        if (uploadError) throw uploadError;

        // Get the public URL
        const {
          data: { publicUrl },
        } = supabase.storage.from("menus").getPublicUrl(storagePath);

        // Determine file type
        const type = file.type.startsWith("image/")
          ? "image"
          : file.type === "application/pdf"
          ? "pdf"
          : "video";

        // Create MediaAsset object
        const mediaAsset: MediaAsset = {
          id: mediaAssetId,
          url: publicUrl,
          type,
          filename: file.name,
          uploadedAt: new Date().toISOString(),
        };

        // Add to menuStore
        await addMediaAsset(menuId, mediaAsset, supabase);

        // Update upload status to success
        setUploadingFiles((prev) =>
          prev.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  status: "success",
                  progress: 100,
                }
              : item
          )
        );

        toast({
          title: "Upload successful",
          description: `${file.name} has been uploaded successfully.`,
        });

        // Remove successful uploads from the list after a delay
        setTimeout(() => {
          setUploadingFiles((prev) =>
            prev.filter((item) => item.id !== fileId || item.status === "error")
          );
        }, 3000);
      } catch (error) {
        console.error("Error uploading file:", error);

        // Update upload status to error
        setUploadingFiles((prev) =>
          prev.map((item) =>
            item.id === fileId
              ? {
                  ...item,
                  status: "error",
                  progress: 100,
                  error:
                    error instanceof Error ? error.message : "Upload failed",
                }
              : item
          )
        );

        toast({
          variant: "destructive",
          title: "Upload failed",
          description:
            error instanceof Error
              ? error.message
              : "There was an error uploading your file.",
        });
      }
    },
    [menuId, supabase, validateFile, sanitizeFileName, addMediaAsset, toast]
  );

  // Update the onDrop function to upload files directly
  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (menuId && supabase && acceptedFiles.length > 0) {
        // Show toast to indicate upload is starting
        toast({
          title: `Uploading ${acceptedFiles.length} file${
            acceptedFiles.length > 1 ? "s" : ""
          }`,
          description: "Your files are being uploaded, please wait...",
        });

        // Upload each file
        acceptedFiles.forEach((file) => {
          uploadFile(file);
        });
      }
    },
    [menuId, supabase, toast, uploadFile]
  );

  // Configure dropzone for drag and drop functionality
  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "image/jpeg": [],
      "image/png": [],
      "image/webp": [],
      "application/pdf": [],
      "video/mp4": [],
      "video/webm": [],
      "video/ogg": [],
    },
    disabled: !isEditable || !menuId || !supabase,
    noClick: true, // Don't trigger file dialog on click since we have our own button
  });

  // Helper to check if uploads are in progress
  const isUploading = uploadingFiles.length > 0;

  return (
    <div className="relative">
      {displayAsset || !isEditable ? (
        // When we have media to display or if it's not editable, use a button
        <button
          className="w-full h-48 sm:h-64 md:h-64 relative md:rounded-none lg:rounded-none xl-rounded-full overflow-hidden transition-transform group"
          onClick={handleViewMedia}
          type="button"
        >
          {displayAsset ? (
            displayAsset.asset.type === "image" ? (
              <>
                <Image
                  src={displayAsset.asset.url}
                  alt={
                    menu?.title && Object.values(menu.title).filter(Boolean)[0]
                      ? `${
                          Object.values(menu.title).filter(Boolean)[0]
                        } menu banner`
                      : menu?.restaurantName
                      ? `${menu.restaurantName} restaurant menu banner`
                      : "Restaurant menu banner"
                  }
                  fill
                  sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 70vw"
                  className="object-cover"
                  priority
                />
                <div className="absolute inset-0 flex justify-end items-end p-4">
                  <div className="flex gap-2">
                    {/* Media Manager Button - Only show when isEditable is true */}
                    {isEditable && (menuId || onMediaManagerClick) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              onClick={handleMediaDialogClick}
                              className="bg-black/60 text-white backdrop-blur-sm w-8 h-8 rounded-full flex items-center justify-center cursor-pointer shadow-sm"
                            >
                              <Pencil className="h-4 w-4" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("MenuBanner.editMedia")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <div
                      onClick={handleViewButtonClick}
                      className="bg-black/60 text-white backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer flex items-center gap-1.5 shadow-sm"
                    >
                      <Images className="h-4 w-4" />
                      <span>
                        {uiLabels?.viewMedia || t("MenuBanner.viewMedia")} (
                        {allAssets.length})
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : displayAsset.asset.type === "video" ? (
              <>
                <video
                  src={displayAsset.asset.url}
                  className="object-cover w-full h-full"
                  autoPlay
                  muted
                  loop
                  playsInline
                />
                <div className="absolute inset-0 flex justify-end items-end p-4">
                  <div className="flex gap-2">
                    {/* Media Manager Button - Only show when isEditable is true */}
                    {isEditable && (menuId || onMediaManagerClick) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              onClick={handleMediaDialogClick}
                              className="bg-black/60 text-white backdrop-blur-sm w-8 h-8 rounded-full flex items-center justify-center cursor-pointer shadow-sm"
                            >
                              <Pencil className="h-4 w-4" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("MenuBanner.editMedia")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                    <div
                      onClick={handleViewButtonClick}
                      className="bg-black/60 text-white backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer flex items-center gap-1.5 shadow-sm"
                    >
                      <Images className="h-4 w-4" />
                      <span>
                        {uiLabels?.viewMedia || t("MenuBanner.viewMedia")} (
                        {allAssets.length})
                      </span>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              // PDF container
              <div className="w-full h-full bg-white flex items-center justify-center relative">
                {/* Use a separate div with pointer-events-none for the PDF preview only */}
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <PDFPreview url={displayAsset.asset.url} />
                </div>
                {/* Absolute positioned div to capture clicks */}
                <div className="absolute inset-0 z-10"></div>
                <div className="absolute inset-0 flex justify-end items-end p-4 z-20">
                  <div className="flex gap-2">
                    <div
                      onClick={handleViewButtonClick}
                      className="bg-black/60 text-white backdrop-blur-sm px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer flex items-center gap-1.5 shadow-sm"
                    >
                      <Images className="h-4 w-4" />
                      <span>
                        {uiLabels?.viewMedia || t("MenuBanner.viewMedia")} (
                        {allAssets.length})
                      </span>
                    </div>

                    {/* Media Manager Button - Only show when isEditable is true */}
                    {isEditable && (menuId || onMediaManagerClick) && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div
                              onClick={handleMediaDialogClick}
                              className="bg-black/60 text-white backdrop-blur-sm w-8 h-8 rounded-full flex items-center justify-center cursor-pointer shadow-sm"
                            >
                              <Pencil className="h-4 w-4" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>{t("MenuBanner.editMedia")}</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : (
            <div className="w-full h-full bg-muted flex flex-col items-center justify-center gap-4">
              <p className="text-muted-foreground">
                {t("MenuBanner.noBannerAvailable")}
              </p>
            </div>
          )}
        </button>
      ) : (
        // When we have no media AND it's editable, use a div to avoid nested buttons
        // And add dropzone functionality
        <div
          className="w-full h-48 sm:h-64 md:h-64 relative md:rounded-none lg:rounded-none xl-rounded-full overflow-hidden"
          {...getRootProps()}
        >
          <input {...getInputProps()} />
          <div
            className={cn(
              "w-full h-full flex flex-col items-center justify-center transition-colors",
              isDragActive
                ? "bg-primary/10 border-2 border-dashed border-primary/50"
                : isUploading
                ? "bg-primary/5"
                : "bg-muted border-2 border-dashed border-muted-foreground/30"
            )}
          >
            {isUploading ? (
              // Show upload progress
              <div className="w-full max-w-xs px-4">
                <div className="flex items-center justify-center mb-4">
                  <Upload className="h-8 w-8 text-primary animate-pulse" />
                </div>
                <h3 className="font-medium text-center text-lg mb-3">
                  {t("MenuBanner.uploading")}
                </h3>
                <div className="space-y-3">
                  {uploadingFiles.map((file) => (
                    <div key={file.id} className="space-y-1">
                      <div className="flex justify-between items-center text-xs">
                        <span
                          className="truncate max-w-[180px]"
                          title={file.file.name}
                        >
                          {file.file.name}
                        </span>
                        <span className="text-muted-foreground">
                          {file.progress}%
                        </span>
                      </div>
                      <Progress value={file.progress} className="h-1" />
                      {file.error && (
                        <p className="text-xs text-destructive">{file.error}</p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ) : isDragActive ? (
              // Drop zone active state
              <div className="text-center">
                <Upload className="h-12 w-12 text-primary mx-auto mb-2" />
                <h3 className="font-medium text-lg">
                  {t("MenuBanner.dropToUpload")}
                </h3>
              </div>
            ) : (
              // Default empty state
              <div className="text-center px-4">
                <div className="flex flex-col items-center">
                  <Images className="h-10 w-10 text-muted-foreground mb-2" />
                  <h3 className="font-medium text-lg mb-1">
                    {t("MenuBanner.addMenuMedia")}
                  </h3>

                  {!isMobileDevice ? (
                    <p className="text-sm text-muted-foreground mb-4">
                      {t("MenuBanner.dragDropInstructions")}
                    </p>
                  ) : (
                    <p className="text-sm text-muted-foreground mb-4">
                      {t("MenuBanner.mobileInstructions")}
                    </p>
                  )}

                  <div className="flex gap-3">
                    <button
                      onClick={handleUploadClick}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:bg-primary/90 font-medium text-sm"
                    >
                      <Upload className="h-4 w-4" />
                      {t("MenuBanner.uploadButton")}
                    </button>
                    <button
                      onClick={handleMediaDialogClick}
                      className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-secondary text-secondary-foreground hover:bg-secondary/90 font-medium text-sm"
                    >
                      <Images className="h-4 w-4" />
                      {t("MenuBanner.mediaLibraryButton")}
                    </button>
                  </div>

                  <p className="text-xs text-muted-foreground mt-4">
                    {t("MenuBanner.formatSupport")}
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Media Uploader Dialog */}
      {menuId && supabase && isEditable && (
        <MediaUploaderDialog
          open={showMediaUploader}
          onOpenChange={setShowMediaUploader}
          menuId={menuId}
          supabase={supabase}
        />
      )}

      {/* Media Manager Dialog */}
      {menuId && supabase && isEditable && (
        <MediaManagerDialog
          open={showMediaManager}
          onOpenChange={setShowMediaManager}
          menuId={menuId}
          supabase={supabase}
        />
      )}
    </div>
  );
}
